import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useAPIGetAttendance } from "../api/api-get-attendance";
import { useAttendanceSaveHandlers } from "../api/use-attendance-save-handlers";
import AttendanceFilter from "./attendance-filter";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabView, TabPanel } from "primereact/tabview";
import useIsMobile from "../../../hooks/useIsMobile";
import {
  isDoctor,
  isNurse,
  isTherapist,
  isCashier,
  isAdmin,
  isBC,
  isOwner,
  isSuperAdmin,
  isFinance,
  isAdminAppointment,
} from "../../../utils/jelim/CheckUserType";

const AttendanceList = forwardRef((props, ref) => {
  const {
    loading,
    listData,
    selectedDate,
    setSelectedDate,
    selectedIds,
    fetchListData,
    handleOnClickRefresh,
    handleToggleCheckbox,
    getSelectedData,
    filterValue,
    setFilterValue,
    vacationMap,
  } = useAPIGetAttendance();

  const {
    savingLoading,
    savingIds,
    handleSaveSelected,
    handleSaveIndividual,
    handleToggleAttend,
  } = useAttendanceSaveHandlers({ selectedDate, fetchListData, getSelectedData });

  const [viewMode, setViewMode] = useState("grid"); // "list" or "grid"
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const isMobile = useIsMobile();

  useImperativeHandle(ref, () => ({}));

  // use effect section

  useEffect(() => {
    fetchListData(selectedDate);
  }, []);

  // Handler section

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    fetchListData(newDate);
  };

  // Filter by user type
  const getFilteredData = (userTypeChecker) => {
    if (!userTypeChecker) return listData;
    return listData.filter(
      (item) => userTypeChecker(item.data_employee?.user_type)
    );
  };

  const renderEmployeeList = (filteredData) => {
    if (loading) {
      return (
        <div className="elevated-card row-center-center w-full p-8" style={{ minHeight: "300px" }}>
          <ProgressSpinner />
        </div>
      );
    }

    if (filteredData.length === 0) {
      return (
        <div className="elevated-card row-center-center w-full p-8" style={{ minHeight: "300px" }}>
          <p className="text-slate-500">No attendance data found</p>
        </div>
      );
    }

    // Force grid view on mobile
    const currentViewMode = isMobile ? "grid" : viewMode;

    return (
      <div
        className={
          currentViewMode === "list"
            ? "col-start-start w-full gap-3"
            : "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 w-full"
        }
        style={{ minHeight: "300px" }}
      >
        {currentViewMode === "list"
          ? filteredData.map((item) => renderEmployeeCardList(item))
          : filteredData.map((item) => renderEmployeeCardGrid(item))}
      </div>
    );
  };

  // Render Employee Card for List View

  const renderEmployeeCardList = (item) => {
    const employee = item.data_employee;
    const isAttending = item.attend === 1;
    const isSaving = savingIds.includes(item.id);

    return (
      <div
        key={item.id}
        className="elevated-card-tight-vertical row-between-center gap-4 w-full"
        onClick={() => {
          if (!isAttending && !isSaving && !item.is_on_vacation) {
            handleToggleCheckbox(item.id);
          }
        }}
        style={{ cursor: !isAttending && !isSaving && !item.is_on_vacation ? 'pointer' : 'default' }}
      >
        {/* Checkbox - Hidden when on vacation or already attending */}
        <div className="row-start-center gap-3">
          <Checkbox
            checked={item.checked || false}
            onChange={(e) => {
              e.stopPropagation();
              handleToggleCheckbox(item.id);
            }}
            style={{ visibility: isAttending || item.is_on_vacation ? 'hidden' : 'visible' }}
          />
        </div>

        {/* Employee Info */}
        <div className="row-start-center gap-4 grow-1 w-full">
          {/* Profile Image */}
          <div className="row-center-center w-16 h-16 rounded-full bg-slate-200 overflow-hidden relative" style={{ flexShrink: 0 }}>
            {employee?.image ? (
              <>
                <img
                  src={employee.image}
                  alt={employee.name}
                  className="w-16 h-16 rounded-full object-cover absolute"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <i className="pi pi-user text-slate-400 text-2xl"></i>
              </>
            ) : (
              <i className="pi pi-user text-slate-400 text-2xl"></i>
            )}
          </div>

          {/* Employee Details */}
          <div className="col-start-start gap-1 grow-1">
            <h3 className="font-bold text-base text-slate-700">
              {employee?.name || "N/A"}
            </h3>
            <p className="text-sm text-slate-500">
              {employee?.code || "No Code"}
            </p>
            <p className="text-sm text-slate-500">
              {employee?.phone_1 || "No Phone"}
            </p>
            <div className="col-start-start gap-2">
              {/* Attendance Status Badge - Hidden when on vacation */}
              {!item.is_on_vacation && (
                <div
                  className={`row-start-center gap-2 px-3 py-1 rounded-full ${isAttending ? "bg-green-100" : "bg-red-100"
                    }`}
                  style={{ width: "fit-content" }}
                >
                  <i
                    className={`${isAttending
                      ? "pi pi-check-circle text-green-600"
                      : "pi pi-times-circle text-red-600"
                      } text-sm`}
                  />
                  <span
                    className={`text-sm font-semibold ${isAttending ? "text-green-700" : "text-red-700"
                      }`}
                  >
                    {isAttending ? "Present" : "Absent"}
                  </span>
                </div>
              )}

              {/* Vacation Badge */}
              {item.is_on_vacation && (
                <div
                  className="row-start-center gap-2 px-3 py-1 rounded-full bg-yellow-100"
                  style={{ width: "fit-content" }}
                >
                  <i className="pi pi-calendar-times text-yellow-600 text-sm" />
                  <span className="text-sm font-semibold text-yellow-700">
                    On Vacation
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons - Hidden when on vacation */}
        {!item.is_on_vacation && (
          <div className="row-end-center gap-2">
            {isAttending ? (
              <Button
                label="Change to Absent"
                icon="pi pi-times"
                outlined
                severity="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleAttend(item);
                }}
                loading={isSaving}
                disabled={isSaving}
              />
            ) : (
              <Button
                label="Mark Present"
                icon="pi pi-check"
                className="bg-indigo-500 border-indigo-500"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveIndividual(item);
                }}
                loading={isSaving}
                disabled={isSaving}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  // Render Employee Card for Grid ViewCheckUserType

  const renderEmployeeCardGrid = (item) => {
    const employee = item.data_employee;
    const isAttending = item.attend === 1;
    const isSaving = savingIds.includes(item.id);

    return (
      <div
        key={item.id}
        className="elevated-card-tight-vertical col-start-stretch gap-3 p-4 w-full"
        style={{
          padding: isMobile ? "12px" : "16px",
          cursor: !isAttending && !isSaving && !item.is_on_vacation ? 'pointer' : 'default'
        }}
        onClick={() => {
          if (!isAttending && !isSaving && !item.is_on_vacation) {
            handleToggleCheckbox(item.id);
          }
        }}
      >
        {/* Checkbox - Hidden when on vacation or already attending */}
        <div className="row-start-center gap-2 w-full">
          <Checkbox
            checked={item.checked || false}
            onChange={(e) => {
              e.stopPropagation();
              handleToggleCheckbox(item.id);
            }}
            style={{ visibility: isAttending || item.is_on_vacation ? 'hidden' : 'visible' }}
          />
        </div>

        {/* Profile Image */}
        <div className="row-center-center w-full">
          <div
            className="row-center-center rounded-full bg-slate-200 overflow-hidden relative"
            style={{
              width: isMobile ? "80px" : "96px",
              height: isMobile ? "80px" : "96px"
            }}
          >
            {employee?.image ? (
              <>
                <img
                  src={employee.image}
                  alt={employee.name}
                  className="rounded-full object-cover absolute"
                  style={{
                    width: isMobile ? "80px" : "96px",
                    height: isMobile ? "80px" : "96px"
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <i className={isMobile ? "pi pi-user text-slate-400 text-3xl" : "pi pi-user text-slate-400 text-4xl"}></i>
              </>
            ) : (
              <i className={isMobile ? "pi pi-user text-slate-400 text-3xl" : "pi pi-user text-slate-400 text-4xl"}></i>
            )}
          </div>
        </div>

        {/* Employee Details */}
        <div className="col-start-center gap-1 w-full">
          <h3 className={isMobile ? "font-bold text-sm text-slate-700 text-center" : "font-bold text-base text-slate-700 text-center"}>
            {employee?.name || "N/A"}
          </h3>
          <p className={isMobile ? "text-xs text-slate-500 text-center" : "text-sm text-slate-500 text-center"}>
            {employee?.code || "No Code"}
          </p>
          <p className={isMobile ? "text-xs text-slate-500 text-center" : "text-sm text-slate-500 text-center"}>
            {employee?.phone_1 || "No Phone"}
          </p>
        </div>

        {/* Attendance Status Badge - Hidden when on vacation */}
        <div className="row-center-center w-full">
          <div className="col-start-center gap-2 w-full">
            {!item.is_on_vacation && (
              <div
                className={`row-start-center gap-2 px-3 py-1 rounded-full ${isAttending ? "bg-green-100" : "bg-red-100"
                  }`}
              >
                <i
                  className={`${isAttending
                    ? "pi pi-check-circle text-green-600"
                    : "pi pi-times-circle text-red-600"
                    } text-sm`}
                />
                <span
                  className={`text-sm font-semibold ${isAttending ? "text-green-700" : "text-red-700"
                    }`}
                >
                  {isAttending ? "Present" : "Absent"}
                </span>
              </div>
            )}

            {/* Vacation Badge */}
            {item.is_on_vacation && (
              <div
                className="row-start-center gap-2 px-3 py-1 rounded-full bg-yellow-100"
              >
                <i className="pi pi-calendar-times text-yellow-600 text-sm" />
                <span className="text-sm font-semibold text-yellow-700">
                  On Vacation
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action Button - Hidden when on vacation */}
        {!item.is_on_vacation && (
          <div className="row-center-center w-full">
            {isAttending ? (
              <Button
                label={isMobile ? "Set Absent" : "Change to Absent"}
                icon="pi pi-times"
                outlined
                severity="info"
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleAttend(item);
                }}
                loading={isSaving}
                disabled={isSaving}
              />
            ) : (
              <Button
                label="Mark Present"
                icon="pi pi-check"
                className="bg-indigo-500 border-indigo-500 w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveIndividual(item);
                }}
                loading={isSaving}
                disabled={isSaving}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="col-start-start w-full gap-4">
      {/* Filter Section */}
      <div className="elevated-card-tight-vertical row-start-start w-full gap-4">
        <AttendanceFilter
          selectedDate={selectedDate}
          setSelectedDate={handleDateChange}
          onClickRefresh={handleOnClickRefresh}
          filterValue={filterValue}
          setFilterValue={setFilterValue}
        />
      </div>

      {/* Action Buttons Section */}
      <div className="elevated-card-tight-vertical w-full gap-4">
        <div className={isMobile ? "col-start-stretch w-full" : "row-between-center gap-4 w-full"}>
          <div className="row-start-center gap-3">
            <span className="text-sm text-slate-500">
              {selectedIds.length} selected
            </span>
            {selectedIds.length > 0 && (
              <Button
                label={isMobile ? "Mark Present" : "Mark Present Selected"}
                icon="pi pi-check"
                className="bg-indigo-500 border-indigo-500"
                onClick={handleSaveSelected}
                disabled={savingLoading}
                loading={savingLoading}
              />
            )}
          </div>

          <div className="row-end-center gap-3">
            {/* View Mode Toggle - Hidden on mobile */}
            {!isMobile && (
              <div className="row-start-center gap-2">
                <Button
                  icon="pi pi-list"
                  outlined={viewMode !== "list"}
                  className={viewMode === "list" ? "bg-indigo-500 border-indigo-500" : ""}
                  onClick={() => setViewMode("list")}
                  tooltip="List View"
                  tooltipOptions={{ position: 'top' }}
                />
                <Button
                  icon="pi pi-th-large"
                  outlined={viewMode !== "grid"}
                  className={viewMode === "grid" ? "bg-indigo-500 border-indigo-500" : ""}
                  onClick={() => setViewMode("grid")}
                  tooltip="Grid View"
                  tooltipOptions={{ position: 'top' }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="elevated-card-tight-vertical w-full" style={{ minHeight: "400px" }}>
        <TabView
          activeIndex={activeTabIndex}
          onTabChange={(e) => setActiveTabIndex(e.index)}
        >
          <TabPanel header="All">
            {renderEmployeeList(listData)}
          </TabPanel>
          <TabPanel header="Doctor">
            {renderEmployeeList(getFilteredData(isDoctor))}
          </TabPanel>
          <TabPanel header="Nurse">
            {renderEmployeeList(getFilteredData(isNurse))}
          </TabPanel>
          <TabPanel header="Therapist">
            {renderEmployeeList(getFilteredData(isTherapist))}
          </TabPanel>
          <TabPanel header="Cashier">
            {renderEmployeeList(getFilteredData(isCashier))}
          </TabPanel>
          <TabPanel header="Admin">
            {renderEmployeeList(getFilteredData(isAdmin))}
          </TabPanel>
          <TabPanel header="BC">
            {renderEmployeeList(getFilteredData(isBC))}
          </TabPanel>
          <TabPanel header="Finance">
            {renderEmployeeList(getFilteredData(isFinance))}
          </TabPanel>
          <TabPanel header="Admin Appt">
            {renderEmployeeList(getFilteredData(isAdminAppointment))}
          </TabPanel>
        </TabView>
      </div>
    </div>
  );
});

export default AttendanceList;
